/**
 * @file ChatCreationModal.js
 * @author Simon Tenedero, Jonas Matulis
 * @created 2024-XX-XX
 * @lastModified 2025-05-27
 * @description file containing ChatCreationModal component
 */

import React, { useRef, useState, useEffect } from "react";
import { fetchPublicUsers } from "../../utils/usersUtils";
import { useUser } from "../../context/UserContext";

/**
 * This component is a modal (popup) that allows users to create a private chat - this appears when the CreatePrivateChatButton component is clicked
 * It displays a form with input fields for the chat name and live URL.
 * It also displays a list of users that can be invited to the chat with search functionality.
 * Users can be selected or removed from the list of invited users.
 * When the create button is clicked, the chat is created with the specified settings.
 *
 * NOTE: right now, we fetch all users everytime this modal is opened, which is not ideal for the quota - might have to figure out a caching strategy later
 *
 * @param {Object} props - The properties passed to the component
 * @param {Function} props.onCreateChat - Function to set the chat settings and create the chat
 * @param {Function} props.onClose - Function to close the chat creation menu
 * @param {Object} props.currentUser - Object with current user info { uid: string, username: string, email: string }
 * @param {Boolean} props.showLoginAlert - state variable to determine if login alert is showing (notify users they must be logged in to create chats)
 * @param {Function} props.setShowLoginAlert - setState function for showLoginAlert
 *
 * @returns {JSX.Element} The rendered ChatCreationModal component
 */
const ChatCreationModal = ({
  onCreateChat,
  onClose,
  currentUser,
  showLoginAlert,
  setShowLoginAlert,
}) => {
  const curVideo = localStorage.getItem("lastVideoUrl") || "";

  const [chatName, setChatName] = useState(""); // State variable for the chat name
  const [chatUrl, setChatUrl] = useState(curVideo); // State variable for the live URL
  const [chatIcon, setChatIcon] = useState(null); // State variable for the chat icon
  const [selectedFile, setSelectedFile] = useState(null); // State variable for selected file
  const [invitedUsers, setInvitedUsers] = useState([]); // State variable for the list of invited users
  const [users, setUsers] = useState([]); // State variable for the list of users
  const [loading, setLoading] = useState(false); // State variable for loading state
  const [searchTerm, setSearchTerm] = useState(""); // State variable for search term

  const { user } = useUser();
  const fileInputRef = useRef(null); // Add ref for the hidden file input

  // Fetch users from the database when the component mounts
  useEffect(() => {
    const loadUsers = async () => {
      const users = await fetchPublicUsers();
      const nonSelfUsers = users.filter((u) => u.uid !== user.uid);
      setUsers(nonSelfUsers);
    };
    loadUsers();
  }, []);

  // Filter users based on search term
  const filteredUsers = users.filter((user) =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Function to add a user to list of invited users
  const handleUserSelect = (user) => {
    if (user.uid !== currentUser.uid) {
      setInvitedUsers((prev) => [...prev, user]);
    }
  };

  // Function to remove a user from the list of invited users
  const handleUserRemove = (user) => {
    setInvitedUsers((prev) => prev.filter((u) => u.uid !== user.uid));
  };

  // Function to create the chat with the specified settings
  const handleCreateChat = async () => {
    const chatSettings = {
      name: chatName,
      url: chatUrl,
      icon: selectedFile,
      invitedUsers,
    };

    try {
      setLoading(true);
      await onCreateChat(chatSettings);
      setLoading(false);
      onClose(); // Close the menu after creating the chat
    } catch (error) {
      console.error("Failed to create chat:", error);
      setLoading(false);
    }
  };

  // Handle file change for chat icon
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setChatIcon(reader.result);
      };
      reader.readAsDataURL(file);
      setSelectedFile(file);
    }
  };

  // Handle removing the selected icon
  const handleRemoveIcon = () => {
    setChatIcon(null);
    setSelectedFile(null);
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle clicking on the preview to select a file
  const handlePreviewClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      {showLoginAlert && (
        <div className="active-users-modal fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
          <div className="modal-content bg-primary rounded-lg shadow-lg p-4 w-full max-w-md dark:bg-gray-800">
            <span
              className="close text-red-500 hover:text-red-800 cursor-pointer float-right"
              onClick={() => {
                setShowLoginAlert(!showLoginAlert);
              }}
            >
              &times;
            </span>
            <h2 className="text-current text-2xl font-semibold m-4 text-center">
              You must Login to create Private Chats!
            </h2>
          </div>
        </div>
      )}

      {currentUser && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-[1005] p-4">
          <div className="bg-secondary p-6 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-scroll relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl font-bold w-6 h-6 flex items-center justify-center"
              title="Close modal"
            >
              ×
            </button>
            <h2 className="text-lg font-semibold mb-4 pr-8">
              Create Private Chat
            </h2>

            {/* Chat Icon Preview Section - Now clickable */}
            <div className="mb-4 text-center">
              <div className="relative inline-block">
                {chatIcon ? (
                  <>
                    <img
                      src={chatIcon}
                      alt="Chat Icon Preview"
                      onClick={handlePreviewClick}
                      className="w-20 h-20 rounded-full object-cover border-2 border-gray-300 shadow-sm cursor-pointer hover:border-blue-400 hover:shadow-md transition-all"
                      title="Click to change icon"
                    />
                    <button
                      onClick={handleRemoveIcon}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors z-10"
                      title="Remove icon"
                    >
                      ×
                    </button>
                  </>
                ) : (
                  <div
                    onClick={handlePreviewClick}
                    className="w-20 h-20 rounded-full bg-gray-300 border-2 border-gray-300 shadow-sm flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-gray-200 transition-all"
                    title="Click to select icon"
                  >
                    <span className="text-2xl font-bold text-gray-600">
                      {chatName ? chatName.charAt(0).toUpperCase() : "?"}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-2 font-medium">
                {chatName || "Chat Name"}
              </p>
              <p className="text-xs text-gray-500">
                Click above to select icon (optional)
              </p>
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />

            {/* Input for chat name */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Chat Name
              </label>
              <input
                type="text"
                placeholder="Enter chat name"
                value={chatName}
                onChange={(e) => setChatName(e.target.value)}
                className="p-2 border rounded w-full"
              />
            </div>

            {/* Input for chat URL */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Live URL</label>
              <input
                type="text"
                placeholder="Enter live URL"
                value={chatUrl}
                onChange={(e) => setChatUrl(e.target.value)}
                className="p-2 border rounded w-full"
              />
            </div>

            <h3 className="font-semibold mb-2">Invite Users</h3>

            {/* Search input for users */}
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-2 p-2 border rounded w-full text-sm"
            />

            <div className="mb-4 max-h-40 overflow-y-scroll">
              {filteredUsers.length === 0 ? (
                <div className="text-gray-500 text-center py-2">
                  {searchTerm
                    ? "No users found matching your search"
                    : "No users available"}
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <div
                    key={user.uid}
                    className="flex items-center justify-between py-2 mb-2 px-4 rounded-xl bg-base-100 hover:bg-base-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <div>
                        {user.profilePicture ? (
                          <img
                            src={user.profilePicture}
                            alt="User Icon"
                            className="w-11 h-11 mr-4 rounded-full object-cover border-2 border-gray-300 shadow-sm flex-shrink-0"
                          />
                        ) : (
                          <div className="w-11 h-11 mr-4 rounded-full bg-gray-300 border-2 border-gray-300 shadow-sm flex items-center justify-center flex-shrink-0">
                            <span className="text-2xl font-bold text-gray-600">
                              {user.username
                                ? user.username.charAt(0).toUpperCase()
                                : "?"}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col">
                        <span className="font-medium">{user.username}</span>
                      </div>
                    </div>

                    {invitedUsers.some((u) => u.uid === user.uid) ? (
                      <button
                        onClick={() => handleUserRemove(user)}
                        className="text-primary hover:text-primary-dark px-2 py-1 text-sm"
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUserSelect(user)}
                        className="hover:text-primary px-2 py-1 text-sm"
                      >
                        Invite
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Show invited users count */}
            {invitedUsers.length > 0 && (
              <div className="mb-4 text-sm text-gray-600">
                ({invitedUsers.length}) user
                {invitedUsers.length !== 1 ? "s" : ""} invited
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <button
                onClick={onClose}
                className="btn btn-neutral px-4 py-2 btn-sm"
              >
                Cancel
              </button>
              <button
                disabled={chatName === "" || !chatUrl || loading}
                onClick={handleCreateChat}
                className="btn btn-primary px-4 py-2 btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Creating...
                  </div>
                ) : (
                  "Create"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatCreationModal;
